// frontend/src/components/liuyao/LiuyaoHistory.jsx
// 六爻卦例历史列表组件
// 功能：显示用户保存的所有六爻卦象、删除功能、详情查看

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { calculateLiuYao } from "./_helper_liuyao.jsx";

export default function LiuyaoHistory({
  currentView,
  setCurrentView,
  isLoggedIn,
  onLogout,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deletingIds, setDeletingIds] = useState([]);

  useEffect(() => {
    fetchMyLiuyao();
  }, [page]);

  const fetchMyLiuyao = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await axios.get(`/api/liuyao/list?page=${page}&per_page=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setCharts(response.data.data);
        setTotalPages(response.data.pages);
      } else {
        setError(response.data.error);
      }
    } catch (err) {
      setError('获取历史记录失败');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 删除卦例
  const handleDeleteChart = async (chart) => {
    const id = chart.id || chart._id || chart.chart_id;
    if (!id) {
      setError("无法删除：记录无 id");
      return;
    }
    const ok = window.confirm("确定要删除该卦例记录吗？此操作不可恢复。");
    if (!ok) return;

    try {
      setDeletingIds((s) => [...s, id]);
      const token = localStorage.getItem("access_token");
      
      const response = await axios.delete(`/api/liuyao/${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200 || response.data.success) {
        fetchMyLiuyao(); // Refresh list
      } else {
        alert('删除失败: ' + response.data.error);
      }
    } catch (err) {
      console.error(err);
      alert('删除失败');
    } finally {
      setDeletingIds((s) => s.filter((x) => x !== id));
    }
  };

    // 查看卦例详情
  const handleViewChart = async (chartId) => {
    if (!chartId) {
      setError("无法打开卦例：缺少 chartId");
      return;
    }

    try {
      // 从后端获取卦例数据
      const token = localStorage.getItem("access_token");
      if (!token) {
        navigate("/login");
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`/api/liuyao/${encodeURIComponent(chartId)}`, {
        method: "GET",
        headers,
        credentials: "include",
      });

      if (res.status === 401) {
        localStorage.removeItem("access_token");
        navigate("/login");
        return;
      }

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`获取卦例详情失败: ${res.status} ${txt}`);
      }

      const data = await res.json();
      const chartData = data.data || data;

      // 构建 requestPayload（从数据库获取的数据）
      const requestPayload = {
        chart_id: chartData.id || "",
        user_id: chartData.user_id || "",
        mingzhuName: chartData.mingzhu_name || "",
        mingzhuGender: chartData.mingzhu_gender || "",
        shudi: chartData.shudi || "",
        zhanti_type: chartData.zhanti_type || "",
        zhanti: chartData.zhanti || "",
        master_record: chartData.master_record || "",
        mingzhu_feedback: chartData.mingzhu_feedback || "",
        datetimeStr: chartData.qiguatime,
        
        // Add birth date fields
        birth_year: chartData.birth_year || "",
        birth_month: chartData.birth_month || "",
        birth_day: chartData.birth_day || "",
        birth_hour: chartData.birth_hour || "",

        method: "saved", // 标记为从保存的卦例打开
        gua_code: chartData.gua_code || "",
      };

      // 如果有 gua_code，尝试从中提取 divinationNumbers
      // gua_code 格式应该是数字字符串，如 "12345"，表示各爻的数值
      let divinationNumbers = [];
      if (chartData.gua_code) {
        divinationNumbers = String(chartData.gua_code)
          .split('')
          .map((ch) => parseInt(ch, 10))
          .filter(num => !isNaN(num));
      }

      // 调用后端计算接口重新计算卦象
      let liuyaoResult = null;
      try {
        if (requestPayload.datetimeStr && divinationNumbers.length > 0) {
          const calcReqData = {
            mingzhuName: requestPayload.mingzhuName,
            mingzhuGender: requestPayload.mingzhuGender,
            zhanti_type: requestPayload.zhanti_type,
            zhanti: requestPayload.zhanti,
            datetimeStr: requestPayload.datetimeStr,
            divinationNumbers: divinationNumbers,
            shudi: requestPayload.shudi
          };

          const calcRes = await axios.post('/api/liuyao/calculate', calcReqData);
          if (calcRes.data.success) {
            liuyaoResult = calcRes.data.data;
            if (chartData.id) liuyaoResult.id = chartData.id;
          } else {
            console.error("后端计算失败:", calcRes.data.error);
          }
        }
      } catch (calcErr) {
        console.error("计算卦象失败:", calcErr);
        // 即使计算失败，仍然导航到详情页，传递已有的数据
      }

      // 导航到详情页，传递数据库数据和计算结果
      navigate(`/liuyao/LiuyaoDetail`, {
        state: {
          requestPayload: requestPayload,
          liuyaoResult: liuyaoResult
          //chartData: chartData, // 也传递原始数据库数据，以便详情页使用
        }
      });

    } catch (err) {
      console.error("打开卦例详情失败:", err);
      setError(err.message || "打开卦例详情失败");
    }
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="返回上一页"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h2 className="text-2xl font-bold text-gray-900">我的六爻排盘</h2>
          </div>
          <button
            onClick={() => navigate('/liuyao/index')}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            新建排盘
          </button>
        </div>

        {loading ? (
          <div className="text-center py-10">加载中...</div>
        ) : error ? (
          <div className="text-center py-10 text-red-600">{error}</div>
        ) : (!charts || charts.length === 0) ? (
          <div className="text-center py-10 text-gray-500">暂无记录</div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {charts.map((chart) => {
                 const id = chart.id || chart._id || chart.chart_id;
                 const hasFeedback = chart.is_feedback ;
                 
                 return (
                <li key={id}>
                  <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium text-indigo-600 truncate">
                            {chart.zhanti_type} - {chart.zhanti}
                            </p>
                            <div className="ml-2 flex-shrink-0 flex">
                                {hasFeedback && (
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                    已反馈
                                    </span>
                                )}
                            </div>
                        </div>
                        
                        <div className="mt-2 flex justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              {chart.mingzhu_name || '未命名'}
                              {chart.mingzhu_gender && <span className="ml-1">({chart.mingzhu_gender === 'male' ? '男' : '女'})</span>}
                            </p>
                            <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                              {chart.qiguatime ? new Date(chart.qiguatime).toLocaleString('zh-CN') : "—"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="ml-5 flex-shrink-0 flex space-x-2">
                        <button
                          onClick={() => handleViewChart(id)}
                          className="px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                        >
                          查看
                        </button>
                        <button
                          onClick={() => handleDeleteChart(chart)}
                          disabled={deletingIds.includes(id)}
                          className={`px-3 py-1 border border-transparent text-sm font-medium rounded-md ${
                            deletingIds.includes(id)
                              ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                              : "text-red-700 bg-red-100 hover:bg-red-200"
                          }`}
                        >
                          {deletingIds.includes(id) ? "删除中" : "删除"}
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              )})}
            </ul>
            
            {/* Pagination */}
            {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                            上一页
                        </button>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                            下一页
                        </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                第 <span className="font-medium">{page}</span> 页，共 <span className="font-medium">{totalPages}</span> 页
                            </p>
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    上一页
                                </button>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    下一页
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
